import { startCase } from 'lodash';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useRecoilCallback,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState
} from 'recoil';

import { Percomatic } from '@biggby-coffee/percomatic-typescript';
import {
	PaymentElement,
	useElements,
	useStripe
} from '@stripe/react-stripe-js';
import {
	PaymentIntentResult,
	StripePaymentElementChangeEvent
} from '@stripe/stripe-js';

import { useLazyGetProfileInfoQuery } from '../../../Account/api';
import { setProfile } from '../../../Account/state/profileSlice';
import { currentTransState } from '../../../atoms';
import { currentTransactionState } from '../../../Cart/atoms';
import { useDeleteCartItem } from '../../../Cart/hooks/useDeleteCartItem';
import { useUpdateSessionCart } from '../../../Cart/hooks/useUpdateSessionCart';
import { setBirthdaySet } from '../../../domains/Auth/slice';
import { setUnavailableProductIds } from '../../../domains/UI/store/slice';
import { useGetUnavailableProductsLazyQuery } from '../../../graphql';
import { activeOrderIdState } from '../../../Order/atoms';
import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { isStoreGlobalState, storeIdState } from '../../../Store/atoms';
import { useStoreLazyQuery } from '../../../Store/graphql';
import { getStoreOpenStatus } from '../../../Store/utils/getStoreOpenStatus';
import { getNow } from '../../../utils/getNow';
// import { on_attempted_checkout } from '../../../utils/gtag';
import {
	checkoutState,
	guestEmailTouchedState,
	guestNameTouchedState,
	guestPhoneTouchedState,
	isCheckoutLoadingState,
	pickupLocationState,
	pickupMethodSubmissionAttemptedState,
	profileStepSubmissionAttemptedState,
	sendOrderErrorState,
	stripeTokenState,
	submissionAttemptedState
} from '../../atoms';
import { Continue } from '../../components/Continue';
import { useSendCustomerOrderMutation } from '../../graphql';
import { useSendTransaction } from '../../hooks/api';
import { SendOrderError } from '../../hooks/useSendOrder';
import { createCustomerOrderInput } from '../../utils/createCustomerOrderInput';

export type PaymentIntentDetails = {
	clientSecret: string | undefined;
	paymentIntentId: string | undefined;
	id: string | undefined;
};
type PaymentTypeProps = {
	paymentType: 'GIFT_CARD' | 'STRIPE' | undefined;
	paymentDetails?: PaymentIntentDetails;
};

export const PaymentGateway = ({
	paymentType,
	paymentDetails
}: PaymentTypeProps): JSX.Element => {
	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// Recoil state
	const storeId = useRecoilValue(storeIdState);
	const isStoreGlobal = useRecoilValue(isStoreGlobalState);
	const state = useRecoilValue(checkoutState);
	const currentTrans = useRecoilValue(currentTransState);
	const { items } = useRecoilValue(currentTransactionState);
	const [loading, setLoading] = useRecoilState(isCheckoutLoadingState);
	const setSendOrderError = useSetRecoilState(sendOrderErrorState);
	const setActiveOrderId = useSetRecoilState(activeOrderIdState);
	const deleteCartItem = useDeleteCartItem();

	// Redux state
	const profileIdFromRedux = useAppSelector(
		state => state.accountReducer.profile.id
	) as unknown as number;
	const appUserIdFromRedux = useAppSelector(state => state.authReducer.id);
	const giftCardPayments = useAppSelector(
		state => state.checkoutReducer.giftCardPayments
	);
	const balanceDue = useAppSelector(
		state => +state.checkoutReducer.balanceDue
	);
	// const isAuthenticated = useAppSelector(
	// 	state => state.authReducer.accessToken
	// );
	const guestName = useAppSelector(state => state.checkoutReducer.firstName);
	const guestPhone = useAppSelector(state => state.checkoutReducer.phone);
	const guestEmail = useAppSelector(state => state.checkoutReducer.email);

	// Local state
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
	const [isPaymentMethodValid, setIsPaymentMethodValid] = useState(true);

	// Hooks
	const updateSessionCart = useUpdateSessionCart();
	const sendTransaction = useSendTransaction(updateSessionCart);
	const [sendOrder] = useSendCustomerOrderMutation();
	const [getProfileInfo] = useLazyGetProfileInfoQuery();

	const [getUnavailableProducts] = useGetUnavailableProductsLazyQuery({
		fetchPolicy: 'network-only'
	});

	const [getStoreStatus] = useStoreLazyQuery({
		fetchPolicy: 'network-only'
	});

	const checkStoreStatus = async (): Promise<boolean> => {
		const { data } = await getStoreStatus({
			variables: { storeId: storeId }
		});
		const storeOpenStatus =
			data?.store && getStoreOpenStatus(data?.store, getNow());
		if (storeOpenStatus !== 'OPEN') {
			throw new SendOrderError(
				'This store is currently not accepting online orders. Please select a different store'
			);
		}
		if (!data?.store?.oLOEnabled) {
			throw new SendOrderError(
				'Could not complete checkout as store is closed or about to close. Please select a different store by selecting the store locator.'
			);
		}

		return true;
	};

	const checkUnavailableProducts = async (): Promise<boolean> => {
		const { data } = await getUnavailableProducts({
			variables: { storeId: storeId }
		});

		const unavailableProductIds =
			data?.unavailableProducts?.map(product => product.productId) || [];

		// Dispatch the unavailable product IDs to redux
		dispatch(setUnavailableProductIds(unavailableProductIds));

		// Check if any items in the cart are unavailable
		if (
			items.some(item =>
				unavailableProductIds.includes(Number(item.product.id))
			)
		) {
			const unavailableProducts = items.filter(item =>
				unavailableProductIds.includes(Number(item.product.id))
			);

			unavailableProducts.forEach(product => {
				deleteCartItem(product.id);
			});

			setSendOrderError(
				new SendOrderError(
					`We've removed the unavailable item(s) from your cart: ${[...new Set(unavailableProducts.map(item => item.product.name))].join(', ')}. Please select a different store or product to proceed with checkout.`
				)
			);
			navigate('/cart');
			return false;
		}
		return true;
	};

	const resetCheckoutState = useRecoilCallback(({ reset }) => () => {
		reset(stripeTokenState);
		reset(pickupLocationState);
		reset(submissionAttemptedState);
		reset(profileStepSubmissionAttemptedState);
		reset(guestNameTouchedState);
		reset(guestEmailTouchedState);
		reset(guestPhoneTouchedState);
		reset(pickupMethodSubmissionAttemptedState);
		reset(sendOrderErrorState);
		reset(isCheckoutLoadingState);
	});

	const dispatchProfile = useCallback(async () => {
		const res = await getProfileInfo(null);
		if (res.data) {
			const userInfo = res.data.data;
			dispatch(setProfile(userInfo.profile as ProfileState));
			dispatch(setBirthdaySet(userInfo.birthday_set));
		}
	}, [dispatch, getProfileInfo]);

	const handlePayment = async (
		paymentMethod: 'GIFT_CARD' | 'STRIPE'
	): Promise<boolean> => {
		try {
			if (loading) return false;

			// Run checkUnavailableProducts and checkStoreStatus concurrently
			setLoading(true);

			if (paymentMethod === 'STRIPE') {
				if (!elements || !stripe) {
					// Stripe.js hasn't yet loaded.
					// Make sure to disable form submission until Stripe.js has loaded.
					return false;
				}
				const { error } = await elements.submit();
				if (error) {
					throw new SendOrderError(
						error.message ||
							'Please verify your payment information.'
					);
				}
			} else {
				if (giftCardPayments.length === 0)
					throw new SendOrderError(
						'Please select any BIGGBY Card to proceed checkout.'
					);
			}

			const indexed: { [key: string]: string } = {};
			items.forEach(item => {
				indexed[item.product.name] = item.product.name;
			});
			// const productNames = Object.keys(indexed).join(', ');
			// on_attempted_checkout({
			// 	checkout_products_name: productNames,
			// 	user_type: isAuthenticated ? 'logged_in' : 'guest'
			// });

			// Check if products are available and store is open
			const [productsValid, storeValid] = await Promise.all([
				checkUnavailableProducts(),
				checkStoreStatus()
			]);

			if (productsValid && storeValid) {
				return await processPayment(paymentMethod);
			} else {
				setLoading(false);
				return false;
			}
		} catch (err) {
			const errorString = err
				? err.toString()
				: 'Something went wrong. Please try again.';

			setSendOrderError(new SendOrderError(errorString));
			setLoading(false);
			return false;
		}
	};

	const prepareCheckoutState = () => {
		state.pickupName = guestName || '';
		state.guestEmail = guestEmail || '';
		state.phone = guestPhone || '';

		state.giftCardPayments = giftCardPayments;
		state.balanceDue = balanceDue;

		if (!state.phone || !state.pickupName) {
			throw new SendOrderError('Please check name and phone.');
		}
	};

	const processPayment = async (
		paymentMethod: 'GIFT_CARD' | 'STRIPE'
	): Promise<boolean> => {
		if (!currentTrans) {
			throw new SendOrderError(
				'No transaction to send. "currentTrans" is undefined.'
			);
		}

		if (isStoreGlobal) {
			throw new SendOrderError(
				'Cannot send an order to the global online ordering store.'
			);
		}

		prepareCheckoutState();

		let paymentResult: PaymentIntentResult | undefined;
		if (paymentMethod === 'STRIPE') {
			if (!stripe || !elements) {
				return false;
			}
			paymentResult = await stripe.confirmPayment({
				elements,
				redirect: 'if_required'
			});
			const paymentIntentId = paymentResult.paymentIntent?.id;

			if (
				paymentResult.paymentIntent &&
				paymentResult.paymentIntent.status === 'succeeded'
			) {
				return completeOrder(
					currentTrans,
					paymentIntentId,
					paymentDetails
				);
			} else if (paymentResult.error) {
				throw new SendOrderError(
					paymentResult.error.message || 'Payment unsuccessful.'
				);
			}
		} else if (paymentMethod === 'GIFT_CARD') {
			return completeOrder(currentTrans, undefined);
		}

		return true;
	};

	const completeOrder = async (
		currentTrans: Percomatic.Serialized.Transaction,
		paymentIntentId: string | undefined,
		paymentDetails?: PaymentIntentDetails
	): Promise<boolean> => {
		// convert to payload type for graphql
		const appUserId = appUserIdFromRedux && +appUserIdFromRedux;

		const profileId =
			typeof profileIdFromRedux === 'number'
				? profileIdFromRedux
				: undefined;

		const order = createCustomerOrderInput(
			state,
			currentTrans,
			paymentIntentId,
			profileId,
			appUserId
		);
		if (paymentDetails) {
			order.id = paymentDetails?.id;
			order.paymentIntentId = paymentDetails?.paymentIntentId;
		}
		const res = await sendOrder({ variables: { order, storeId } });

		if (res.errors) {
			throw new SendOrderError(res.errors[0].message);
		}

		if (!res.data) {
			throw new SendOrderError('No data.');
		}

		if (res.data.sendCustomerOrder.status.status === 'ORDER_ERROR') {
			throw new SendOrderError('Unable to process order.');
		}

		setActiveOrderId(res.data.sendCustomerOrder.id);
		navigate('/orders/active');
		sendTransaction();
		resetCheckoutState();
		setLoading(false);

		if (appUserId) {
			dispatchProfile();
		}
		return true;
	};

	const handlePaymentMethodChange = (
		event: StripePaymentElementChangeEvent
	) => {
		setIsPaymentMethodValid(event.complete);
		setSelectedPaymentMethod(event.value.type);
	};

	return (
		<>
			{paymentType === 'STRIPE' ? (
				<>
					<PaymentElement
						onChange={handlePaymentMethodChange}
						options={{
							layout: {
								type: 'accordion',
								defaultCollapsed: true,
								radios: false,
								spacedAccordionItems: true
							}
						}}
					/>
					<Continue
						back
						clickHandler={() => handlePayment('STRIPE')}
						loading={loading}
						backLabel={'BACK'}
						continueLabel={
							isPaymentMethodValid
								? `COMPLETE ORDER`
								: `PAY WITH ${startCase(selectedPaymentMethod).toUpperCase()}`
						}
					/>
				</>
			) : (
				<Continue
					back
					clickHandler={() => handlePayment('GIFT_CARD')}
					loading={loading}
					disableOrder={balanceDue != 0}
					continueLabel="COMPLETE ORDER"
					backLabel="BACK"
				/>
			)}
		</>
	);
};
